/*
Textes des pages d'atterrissage CMS v5 : activité (/activite), ville (/ville), type de lieu (/type).
Même mécanique que la homepage : une clé `<catégorie>:v5.<path>` dans l'API i18n prend le dessus,
sinon le dictionnaire local de la catégorie, sinon le tronc commun (fr / en / es).
`tx(path, vars)` remplace les `{n}` / `{name}` par les valeurs passées.
*/
import getValue from 'get-value';
import { useTranslation } from '@/helpers/translate';

const COMMON = {
  fr: {
    sceneLabel: 'Scène',
    subtitle: "Les lieux de milliers d'hôtes deviennent la scène de vos productions captivantes.",
    features: ['Plus de 10 000 lieux référencés', 'Votre décor idéal en un clin d’œil', 'Échangez avec les hôtes, réservez dans l’instant'],
    proof: { count: '50 000+', label: 'productions' },
    intro: { kicker: 'en bref' },
    spaces: {
      kicker: 'lieux disponibles',
      count: '{n} lieux',
      page: 'page {n}',
      all: 'Tous les lieux',
    },
    guide: { kicker: 'le guide', title: 'Tout ce qu’il faut savoir.' },
    brief: {
      kicker: 'accompagnement expert',
      title: 'Le décor idéal n’est pas encore en ligne ?',
      desc: 'Un interlocuteur dédié étudie votre brief et vous propose une sélection adaptée à votre projet et à votre budget, sans surcoût.',
      cta: 'Confiez votre brief',
      imageAlt: 'L’équipe We Are Scene vous accompagne',
    },
    cta: { title: 'Votre prochaine scène commence ici.', search: 'Rechercher un lieu', brief: 'Confiez votre brief' },
  },
  en: {
    sceneLabel: 'Scene',
    subtitle: 'The places of thousands of hosts become the stage of your captivating productions.',
    features: ['10,000+ listed locations', 'Your ideal set in a glance', 'Talk to hosts, book instantly'],
    proof: { count: '50,000+', label: 'productions' },
    intro: { kicker: 'in short' },
    spaces: {
      kicker: 'available locations',
      count: '{n} locations',
      page: 'page {n}',
      all: 'All locations',
    },
    guide: { kicker: 'the guide', title: 'Everything you need to know.' },
    brief: {
      kicker: 'expert support',
      title: 'The ideal set is not online yet?',
      desc: 'A dedicated contact studies your brief and puts together a selection tailored to your project and budget, at no extra cost.',
      cta: 'Send us your brief',
      imageAlt: 'The We Are Scene team supports you',
    },
    cta: { title: 'Your next scene starts here.', search: 'Search a location', brief: 'Send us your brief' },
  },
  es: {
    sceneLabel: 'Escena',
    subtitle: 'Los lugares de miles de anfitriones se convierten en el escenario de tus producciones.',
    features: ['Más de 10.000 lugares referenciados', 'Tu decorado ideal en un instante', 'Habla con los anfitriones y reserva al momento'],
    proof: { count: '50.000+', label: 'producciones' },
    intro: { kicker: 'en resumen' },
    spaces: {
      kicker: 'lugares disponibles',
      count: '{n} lugares',
      page: 'página {n}',
      all: 'Todos los lugares',
    },
    guide: { kicker: 'la guía', title: 'Todo lo que hay que saber.' },
    brief: {
      kicker: 'acompañamiento experto',
      title: '¿El decorado ideal aún no está en línea?',
      desc: 'Un interlocutor dedicado estudia tu brief y te propone una selección adaptada a tu proyecto y presupuesto, sin coste adicional.',
      cta: 'Envíanos tu brief',
      imageAlt: 'El equipo de We Are Scene te acompaña',
    },
    cta: { title: 'Tu próxima escena empieza aquí.', search: 'Buscar un lugar', brief: 'Envíanos tu brief' },
  },
};

// Ce qui change d'une catégorie à l'autre : l'étiquette du hero, le titre de la grille de lieux
// (`{name}` = linktext du document CMS) et l'intitulé des liens croisés.
const BY_CATEGORY = {
  activity: {
    fr: { kicker: 'activité', spaces: { title: 'Des lieux prêts pour vos {name}.' }, others: { kicker: 'autres activités' } },
    en: { kicker: 'activity', spaces: { title: 'Locations ready for your {name}.' }, others: { kicker: 'other activities' } },
    es: { kicker: 'actividad', spaces: { title: 'Lugares listos para tus {name}.' }, others: { kicker: 'otras actividades' } },
  },
  city: {
    fr: { kicker: 'ville', spaces: { title: 'Des lieux prêts pour vos productions à {name}.' }, others: { kicker: 'autres villes' } },
    en: { kicker: 'city', spaces: { title: 'Locations ready for your productions in {name}.' }, others: { kicker: 'other cities' } },
    es: { kicker: 'ciudad', spaces: { title: 'Lugares listos para tus producciones en {name}.' }, others: { kicker: 'otras ciudades' } },
  },
  type: {
    fr: { kicker: 'type de lieu', spaces: { title: 'Des lieux de type {name}, prêts pour vos productions.' }, others: { kicker: 'autres types de lieux' } },
    en: { kicker: 'venue type', spaces: { title: '{name} locations, ready for your productions.' }, others: { kicker: 'other venue types' } },
    es: { kicker: 'tipo de lugar', spaces: { title: 'Lugares de tipo {name}, listos para tus producciones.' }, others: { kicker: 'otros tipos de lugares' } },
  },
};

function interpolate(value, vars) {
  if (typeof value !== 'string' || !vars) return value;
  return Object.keys(vars).reduce((acc, key) => acc.split(`{${key}}`).join(String(vars[key])), value);
}

export function useLandingCopy(category) {
  const { t, lang } = useTranslation();
  const dicts = [BY_CATEGORY[category]?.[lang], COMMON[lang], BY_CATEGORY[category]?.fr, COMMON.fr];

  const tx = (path, vars) => {
    const key = `${category}:v5.${path}`;
    const fromApi = t(key);
    if (fromApi && fromApi !== key) return interpolate(fromApi, vars);
    const local = dicts.map((dict) => (dict ? getValue(dict, path) : undefined)).find((v) => v !== undefined);
    return interpolate(local, vars);
  };

  return { tx, lang };
}

export default useLandingCopy;
